import { useState } from 'react'
import StickerArt from './StickerArt'

/**
 * Médias produit : images réelles si fournies, sinon motifs SVG stickers.
 * @param {{
 *   product: {
 *     id: string,
 *     images?: string[],
 *     motifs?: string[],
 *     accent?: string,
 *     glow?: string,
 *   },
 *   alt: string,
 *   className?: string,
 * }} props
 */
export default function ProductMedia({ product, alt, className = '' }) {
  const images = Array.isArray(product.images) ? product.images.filter(Boolean) : []
  const hasImages = images.length > 0
  const motifs = product.motifs || ['leaf']
  const [active, setActive] = useState(0)

  if (hasImages) {
    const safeIndex = Math.min(active, images.length - 1)
    return (
      <div className={className}>
        <div className="relative aspect-square overflow-hidden rounded-[1.75rem] border border-ng-dark/8 bg-white shadow-sm">
          <img
            src={images[safeIndex]}
            alt={alt}
            className="h-full w-full object-contain p-4 sm:p-6"
            loading="eager"
            decoding="async"
          />
        </div>
        {images.length > 1 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {images.map((src, index) => (
              <button
                key={src}
                type="button"
                onClick={() => setActive(index)}
                className={`h-16 w-16 overflow-hidden rounded-xl border bg-white p-1 transition sm:h-18 sm:w-18 ${
                  index === safeIndex
                    ? 'border-ng-mid ring-2 ring-ng-mid/30'
                    : 'border-ng-dark/10 hover:border-ng-dark/25'
                }`}
                aria-label={`${alt} — ${index + 1}`}
                aria-current={index === safeIndex}
              >
                <img src={src} alt="" className="h-full w-full object-contain" loading="lazy" />
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  // Fallback stickers SVG (en attendant les vraies photos)
  return (
    <div className={className}>
      <div className="relative overflow-hidden rounded-[1.75rem] border border-ng-dark/8 bg-gradient-to-br from-white via-ng-beige to-[#e7f4ea] p-8 shadow-sm sm:p-12">
        <div
          className="pointer-events-none absolute -right-10 -top-10 h-48 w-48 rounded-full opacity-40 blur-3xl"
          style={{ background: product.glow }}
          aria-hidden="true"
        />
        {motifs.length > 1 ? (
          <div className="relative mx-auto grid max-w-md grid-cols-3 gap-3 sm:gap-4">
            {motifs.map((motif, index) => (
              <div key={motif} className={index % 3 === 1 ? 'mt-3' : ''}>
                <StickerArt
                  motif={motif}
                  accent={product.accent}
                  glow={product.glow}
                  rotate={index % 2 === 0 ? -4 : 5}
                  animated
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="relative mx-auto max-w-sm">
            <StickerArt
              motif={motifs[0]}
              accent={product.accent}
              glow={product.glow}
              rotate={-3}
              animated
            />
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Miniature carte catalogue (image ou motif).
 */
export function ProductThumb({ product, alt, className = '', compact = false }) {
  const cover = product.images?.[0]
  const motif = product.motifs?.[0] || 'leaf'
  const imageFit = compact ? 'object-cover object-left' : 'object-contain p-4'

  if (cover) {
    return (
      <div className={`relative aspect-square overflow-hidden bg-white ${className}`}>
        <img
          src={cover}
          alt={alt}
          className={`h-full w-full ${imageFit} transition-transform duration-300 group-hover:scale-105`}
          loading="lazy"
          decoding="async"
        />
      </div>
    )
  }

  return (
    <div className={`relative aspect-square overflow-hidden bg-gradient-to-br from-ng-beige via-white to-[#eef6ea] p-6 ${className}`}>
      <div
        className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full opacity-40 blur-2xl"
        style={{ background: product.glow }}
        aria-hidden="true"
      />
      <div className="relative mx-auto h-full max-w-[200px] transition-transform duration-300 group-hover:scale-105">
        <StickerArt
          motif={motif}
          accent={product.accent}
          glow={product.glow}
          rotate={-3}
          animated
        />
      </div>
    </div>
  )
}
